import React from "react";
import { Box } from "@mui/material";
import Section from "../ui/Section";
import Reveal from "../ui/Reveal";
import { Accent, Display, Label } from "../ui/Type";
import { TESTIMONIALS } from "../../data/company";
import { COMMITMENTS } from "../../data/process";
import { color, font, type } from "../../design/tokens";

/**
 * Customer testimonials — or, until there are any, the commitments block.
 *
 * `TESTIMONIALS` in data/company.js is empty, because no verified customer
 * reviews have been supplied. The correct response to that is not to write
 * four plausible-sounding quotes and attribute them to initials; it's to say
 * what we hold ourselves to and label it as exactly that.
 *
 * Push real reviews into `TESTIMONIALS` and this section switches over on its
 * own, with no component change. Review structured data stays off until then
 * — see the note in seo/schema.js.
 */
const Testimonials = () => {
  const hasReviews = TESTIMONIALS.length > 0;

  return (
    <Section tone="deep">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: 3, md: 8 },
          alignItems: "end",
          marginBottom: { xs: 5, md: 8 },
        }}
      >
        <Box>
          <Label>{hasReviews ? "In their words" : "Our commitments"}</Label>
          <Display as="h2" size="lg" sx={{ marginTop: 3 }}>
            {hasReviews ? (
              <>
                What clients <Accent>say</Accent>.
              </>
            ) : (
              <>
                What working with us <Accent>actually looks like</Accent>.
              </>
            )}
          </Display>
        </Box>
        <Box component="p" sx={{ ...type.body, color: color.graphite, margin: 0, maxWidth: 460 }}>
          {hasReviews
            ? "Comments from homeowners we've worked with across Durham Region."
            : "We'd rather show you four commitments we can be held to than four testimonials nobody can verify. When we have reviews worth publishing, they'll go here instead."}
        </Box>
      </Box>

      {hasReviews ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: { xs: 4, md: 6 },
          }}
        >
          {TESTIMONIALS.map((review, index) => (
            <Reveal key={`${review.name}-${index}`} delay={index * 70}>
              <Box
                component="figure"
                sx={{
                  margin: 0,
                  paddingTop: 4,
                  borderTop: `1px solid ${color.line}`,
                  height: "100%",
                }}
              >
                <Box
                  component="blockquote"
                  sx={{
                    margin: 0,
                    fontFamily: font.display,
                    fontSize: "clamp(1.35rem, 2.2vw, 1.75rem)",
                    lineHeight: 1.35,
                    color: color.ink,
                  }}
                >
                  “{review.quote}”
                </Box>
                <Box
                  component="figcaption"
                  sx={{ ...type.caption, color: color.mute, marginTop: 3 }}
                >
                  {review.name}
                  {review.location ? ` — ${review.location}` : ""}
                  {review.project ? ` · ${review.project}` : ""}
                </Box>
              </Box>
            </Reveal>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
            gap: { xs: 4, md: 5 },
          }}
        >
          {COMMITMENTS.map((item, index) => (
            <Reveal key={item.title} delay={index * 70}>
              <Box sx={{ paddingTop: 3, borderTop: `1px solid ${color.line}`, height: "100%" }}>
                <Box
                  component="h3"
                  sx={{
                    fontFamily: font.display,
                    fontSize: "1.35rem",
                    lineHeight: 1.25,
                    color: color.ink,
                    margin: 0,
                  }}
                >
                  {item.title}
                </Box>
                <Box component="p" sx={{ ...type.bodySm, color: color.graphite, marginTop: 1.5 }}>
                  {item.body}
                </Box>
              </Box>
            </Reveal>
          ))}
        </Box>
      )}
    </Section>
  );
};

export default Testimonials;
